import { Injectable } from '@angular/core';

import { Geolocation } from '@capacitor/geolocation';

import { GeolocationError } from '../../../Domain/Error/geolocation-error';
import { GeolocationService } from '../../../Domain/geolocation.service';
import { GeolocationOptions } from '../../../Domain/Model/geolocation-options';
import { GeolocationPosition } from '../../../Domain/Model/geolocation-position';

/**
 * Capacitor implementation of the GeolocationService using Capacitor's Geolocation plugin
 */
@Injectable()
export class CapacitorGeolocationService extends GeolocationService {
    /**
     * Get the current position using Capacitor's Geolocation plugin
     * @param options Optional configuration for the geolocation request
     * @returns A promise that resolves with the current position
     */
    async getCurrentPosition(options?: GeolocationOptions): Promise<GeolocationPosition> {
        try {
            const position = await Geolocation.getCurrentPosition({
                enableHighAccuracy: options?.enableHighAccuracy ?? true,
                timeout: options?.timeout ?? 10000,
                maximumAge: options?.maximumAge ?? 0
            });

            return {
                coords: {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    accuracy: position.coords.accuracy
                },
                timestamp: position.timestamp
            };
        } catch (error) {
            throw this.mapError(error);
        }
    }

    /**
     * Maps Capacitor geolocation errors to a standardized format
     * @param error The error from Capacitor's Geolocation plugin
     * @returns A standardized GeolocationError
     */
    private mapError(error: unknown): GeolocationError {
        if (!(error instanceof Error) || !error.message) {
            return new GeolocationError('An unexpected error occurred.');
        }

        if (error.message.includes('permission denied')) {
            return new GeolocationError('Permission denied. Please enable location access.');
        } else if (error.message.includes('timeout')) {
            return new GeolocationError('Taking too long to get your location. Please try again.');
        } else {
            return new GeolocationError('An unexpected error occurred.');
        }
    }
}
